import { ActionIcon, Box, Menu, Stack, Tooltip } from '@mantine/core';
import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { useRef } from 'react';

import { COPY_EXCLUDE_ATTR } from '../MarkdownRenderer.constants';
import { useMarkdownRendererContext } from '../MarkdownRenderer.context';
import { parseTableData, toCsv, toMarkdown, triggerDownload } from '../utils';

import type { ReactNode, TableHTMLAttributes } from 'react';

type CustomTableProps = TableHTMLAttributes<HTMLTableElement> & {
  children?: ReactNode;
};

const CustomTable = ({ children, ...props }: CustomTableProps): ReactNode => {
  const tableRef = useRef<HTMLTableElement>(null);
  const { diffSources } = useMarkdownRendererContext();
  const isDiffMode = diffSources !== undefined;

  const downloadAsCSV = (): void => {
    if (!tableRef.current) return;
    const data = parseTableData(tableRef.current);
    triggerDownload('\uFEFF' + toCsv(data), 'table-data.csv', 'text/csv');
  };

  const downloadAsMarkdown = (): void => {
    if (!tableRef.current) return;
    const data = parseTableData(tableRef.current);
    triggerDownload(toMarkdown(data), 'table-data.md', 'text/markdown');
  };

  return (
    <Stack gap="xs" className="preserve-styles">
      <Box style={{ overflowX: 'auto' }}>
        <table ref={tableRef} {...props}>
          {children}
        </table>
      </Box>
      <Box {...{ [COPY_EXCLUDE_ATTR]: true }}>
        <Tooltip
          label="Download is disabled while viewing changes"
          disabled={!isDiffMode}
        >
          <Menu shadow="sm" width={120} position="bottom-start">
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                title="Download table"
                disabled={isDiffMode}
              >
                <DownloadSimpleIcon size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={downloadAsCSV}>CSV</Menu.Item>
              <Menu.Item onClick={downloadAsMarkdown}>Markdown</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Tooltip>
      </Box>
    </Stack>
  );
};

export default CustomTable;
